/* eslint-disable no-console */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import styled from "styled-components";
import {
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch
} from "@mui/material";
import { ExpandMore, CloudUpload, Storage } from "@mui/icons-material";
import { THEME_MAP } from "../theme";
import { functions, firestore } from "../services/firebase";
import { captureError } from "../lib/errorReporting";
import BarRecordsPreview from "../components/MigrationAdmin/BarRecordsPreview";
import DeleteRowsDialog from "../components/OSMDataAdmin/DeleteRowsDialog";
const Container = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const StyledCard = styled(Card)`
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const StatusChip = styled(Chip)`
  margin: 4px;
`;

const ResultContainer = styled(Box)`
  margin-top: 16px;
  padding: 16px;
  background-color: ${THEME_MAP["color-basic-100"]};
  border-radius: 8px;
  max-height: 400px;
  overflow-y: auto;
`;

export function MigrationAdmin() {
  // Fixed THEME_MAP reference issue
  const [barId, setBarId] = useState("");
  const [batchSize, setBatchSize] = useState(1);
  const [migrateAll, setMigrateAll] = useState(false);
  const [onlyUnmigrated, setOnlyUnmigrated] = useState(true);
  const [forceMigration, setForceMigration] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [barsData, setBarsData] = useState([]);
  const [loadingBarsData, setLoadingBarsData] = useState(false);
  const [selectedBarRows, setSelectedBarRows] = useState(new Set());
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [promoteForceUpsert, setPromoteForceUpsert] = useState(false);
  const [promotionResult, setPromotionResult] = useState(null);
  const [promotionError, setPromotionError] = useState(null);
  const [promotionLoading, setPromotionLoading] = useState(false);

  const migrationFunction = useMemo(() => functions.httpsCallable("migrateToSupabase"), []);
  const listTempBarsFunction = useMemo(() => functions.httpsCallable("listTempBars"), []);
  const deleteTempBarsFunction = useMemo(() => functions.httpsCallable("deleteTempBars"), []);
  const promoteTempBarsFunction = useMemo(() => functions.httpsCallable("promoteTempBars"), []);

  const fetchBarsData = useCallback(async () => {
    setLoadingBarsData(true);
    try {
      const response = await listTempBarsFunction({ includeCategories: true });
      const stagedBars = [...(response?.data?.bars || [])];
      stagedBars.sort((a, b) => {
        const nameA = (a.bar_name || "").toLowerCase();
        const nameB = (b.bar_name || "").toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setBarsData(stagedBars);
      setSelectedBarRows((prev) => {
        const validIds = new Set(stagedBars.map((item) => item.bar_id));
        const filtered = Array.from(prev).filter((id) => validIds.has(id));
        return new Set(filtered);
      });
    } catch (fetchError) {
      captureError(fetchError, { view: "MigrationAdmin", action: "fetchStagedBars" });
      console.error("Error fetching staged bars:", fetchError);
      setError(fetchError.message || "Failed to fetch staged bar records");
    } finally {
      setLoadingBarsData(false);
    }
  }, [listTempBarsFunction]);

  useEffect(() => {
    fetchBarsData();
  }, [fetchBarsData]);

  const handleSelectAllBars = (event) => {
    if (event.target.checked) {
      setSelectedBarRows(new Set(barsData.map((bar) => bar.bar_id)));
    } else {
      setSelectedBarRows(new Set());
    }
  };

  const handleToggleBarSelection = (id) => {
    setSelectedBarRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleDeleteSelectedBars = async () => {
    setShowDeleteDialog(false);
    if (selectedBarRows.size === 0) {
      return;
    }

    try {
      const barIds = Array.from(selectedBarRows);
      await deleteTempBarsFunction({ barIds });

      const batch = firestore.batch();
      barIds.forEach((barId) => {
        batch.delete(firestore.collection("bars").doc(barId));
        batch.delete(firestore.collection("Bars").doc(barId));
      });

      await batch.commit();
      setSelectedBarRows(new Set());
      fetchBarsData();
    } catch (deleteError) {
      captureError(deleteError, { view: "MigrationAdmin", action: "deleteStagedBars" });
      console.error("Failed to delete staged bar records:", deleteError);
      setError(deleteError.message || "Failed to delete selected bar records");
    }
  };

  const handleMigration = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const payload = migrateAll 
        ? { 
            batchSize: batchSize,
            onlyUnmigrated: onlyUnmigrated,
            forceMigration: forceMigration 
          }
        : { 
            barId: barId.trim(), 
            batchSize: 1,
            forceMigration: forceMigration 
          };

      console.log("Starting migration with payload:", payload);
      
      const response = await migrationFunction(payload);
      
      console.log("Migration response:", response);
      setResult(response.data);
      fetchBarsData();
      
    } catch (err) {
      captureError(err, { view: "MigrationAdmin", action: "runMigration" });
      console.error("Migration error:", err);
      setError(err.message || "Migration failed");
    } finally {
      setLoading(false);
    }
  };

  const handlePromoteTempBars = async () => {
    setPromotionLoading(true);
    setPromotionError(null);
    setPromotionResult(null);

    try {
      const response = await promoteTempBarsFunction({ forceUpsert: promoteForceUpsert });
      setPromotionResult(response.data);

      if (!response.data?.errors || response.data.errors.length === 0) {
        setSelectedBarRows(new Set());
        fetchBarsData();
      }
    } catch (err) {
      captureError(err, { view: "MigrationAdmin", action: "promoteTempBars" });
      console.error("Promotion error:", err);
      setPromotionError(err.message || "Promotion failed");
    } finally {
      setPromotionLoading(false);
    }
  };

  const isFormValid = () => {
    if (migrateAll) {
      return batchSize > 0 && batchSize <= 500;
    }
    return barId.trim().length > 0;
  };

  return (
    <Container>
      <Typography variant="h4" gutterBottom color="primary">
        <Storage sx={{ mr: 1, verticalAlign: "middle" }} />
        Supabase Migration Admin
      </Typography>
      
      <Typography variant="body1" gutterBottom sx={{ mb: 3 }}>
        Migrate bar data from Firestore to Supabase PostgreSQL database with PostGIS support.
      </Typography>

      <StyledCard>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Migration Configuration
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={migrateAll}
                  onChange={(e) => setMigrateAll(e.target.checked)}
                  color="primary"
                />
              }
              label="Migrate All Bars (Batch Mode)"
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={forceMigration}
                  onChange={(e) => setForceMigration(e.target.checked)}
                  color="warning"
                />
              }
              label="Force Migration (Re-migrate already migrated bars)"
            />
          </Box>

          {migrateAll && (
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={onlyUnmigrated}
                    onChange={(e) => setOnlyUnmigrated(e.target.checked)}
                    color="primary"
                    disabled={forceMigration}
                  />
                }
                label="Only Unmigrated Bars (Skip already migrated)"
              />
            </Box>
          )}

          {!migrateAll && (
            <TextField
              fullWidth
              label="Bar ID"
              value={barId}
              onChange={(e) => setBarId(e.target.value)}
              placeholder="e.g., vMIy6rTSUJO1JtmbrKo7"
              helperText="Enter the Firestore document ID of the bar to migrate"
              sx={{ mb: 2 }}
            />
          )}

          {migrateAll && (
            <TextField
              fullWidth
              type="number"
              label="Batch Size"
              value={batchSize}
              onChange={(e) => setBatchSize(parseInt(e.target.value) || 1)}
              inputProps={{ min: 1, max: 500 }}
              helperText="Number of bars to migrate in this batch (max 500)"
              sx={{ mb: 2 }}
            />
          )}

          <Button
            variant="contained"
            color="primary"
            onClick={handleMigration}
            disabled={!isFormValid() || loading}
            startIcon={loading ? <CircularProgress size={20} /> : <CloudUpload />}
            fullWidth
            size="large"
          >
            {loading 
              ? "Migrating..." 
              : migrateAll 
                ? forceMigration 
                  ? `Force Migrate ${batchSize} Bars`
                  : onlyUnmigrated 
                    ? `Migrate ${batchSize} Unmigrated Bars`
                    : `Migrate ${batchSize} Bars`
                : forceMigration
                  ? "Force Migrate Single Bar"
                  : "Migrate Single Bar"
            }
          </Button>
        </CardContent>
      </StyledCard>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>Migration Failed:</strong> {error}
        </Alert>
      )}

      {result && (
        <StyledCard>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Migration Results
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <StatusChip 
                label={`Status: ${result.result}`}
                color={result.result === "success" ? "success" : "error"}
              />
              <StatusChip 
                label={`Environment: ${result.environment}`}
                color="info"
              />
              <StatusChip 
                label={`Total: ${result.migrated}`}
                color="success"
              />
              {result.inserted !== undefined && (
                <StatusChip 
                  label={`New: ${result.inserted}`}
                  color="primary"
                />
              )}
              {result.updated !== undefined && (
                <StatusChip 
                  label={`Updated: ${result.updated}`}
                  color="warning"
                />
              )}
              <StatusChip 
                label={`Failed: ${result.failed}`}
                color={result.failed > 0 ? "error" : "default"}
              />
            </Box>

            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Message:</strong> {result.message}
            </Typography>

            {result.errors && result.errors.length > 0 && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography color="error">
                    Errors ({result.errors.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <ResultContainer>
                    {result.errors.map((error, index) => (
                      <Alert key={index} severity="error" sx={{ mb: 1 }}>
                        {typeof error === "string" ? error : JSON.stringify(error, null, 2)}
                      </Alert>
                    ))}
                  </ResultContainer>
                </AccordionDetails>
              </Accordion>
            )}
          </CardContent>
        </StyledCard>
      )}

      <BarRecordsPreview
        barRecords={barsData}
        selectedRows={selectedBarRows}
        onSelectAll={handleSelectAllBars}
        onSelectRow={handleToggleBarSelection}
        onRequestDelete={() => setShowDeleteDialog(true)}
        onRefresh={fetchBarsData}
        loading={loadingBarsData}
      />

      <StyledCard>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Promote Staged Bars
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Move vetted records from Supabase `temp_bars` into the production `bars` table. Successful runs clear the staging tables.
          </Typography>

          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={promoteForceUpsert}
                  onChange={(event) => setPromoteForceUpsert(event.target.checked)}
                  color="warning"
                />
              }
              label="Force Upsert (overwrite existing bars)"
            />
          </Box>

          {promotionError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <strong>Promotion Failed:</strong> {promotionError}
            </Alert>
          )}

          {promotionResult && (
            <Box sx={{ mb: 2 }}>
              <StatusChip label={`Promoted: ${promotionResult.promoted || 0}`} color="success" />
              <StatusChip label={`Inserted: ${promotionResult.inserted || 0}`} color="primary" />
              <StatusChip label={`Updated: ${promotionResult.updated || 0}`} color="warning" />
              <StatusChip label={`Skipped: ${promotionResult.skipped || 0}`} color="default" />
              <StatusChip
                label={promotionResult.clearedTempBars ? "Staging cleared" : "Staging not cleared"}
                color={promotionResult.clearedTempBars ? "success" : "default"}
              />
            </Box>
          )}

          {promotionResult?.message && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>Summary:</strong> {promotionResult.message}
            </Typography>
          )}

          {promotionResult?.errors?.length > 0 && (
            <Accordion sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography color="error">Errors ({promotionResult.errors.length})</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <ResultContainer>
                  {promotionResult.errors.map((promotionErr, index) => (
                    <Alert key={index} severity="error" sx={{ mb: 1 }}>
                      {typeof promotionErr === "string" ? promotionErr : JSON.stringify(promotionErr, null, 2)}
                    </Alert>
                  ))}
                </ResultContainer>
              </AccordionDetails>
            </Accordion>
          )}

          <Button
            variant="contained"
            color="secondary"
            onClick={handlePromoteTempBars}
            disabled={promotionLoading}
            startIcon={promotionLoading ? <CircularProgress size={20} /> : <CloudUpload />}
            fullWidth
            size="large"
          >
            {promotionLoading ? "Promoting..." : promoteForceUpsert ? "Force Promote Staged Bars" : "Promote Staged Bars"}
          </Button>
        </CardContent>
      </StyledCard>

      <DeleteRowsDialog
        open={showDeleteDialog}
        selectedCount={selectedBarRows.size}
        onCancel={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteSelectedBars}
        resourceLabel="the Supabase temp_bars staging table and linked Firestore bar documents"
        warningText="This permanently removes the staged data and its Firestore source."
        confirmLabel={`Yes, Delete ${selectedBarRows.size} Record${selectedBarRows.size !== 1 ? "s" : ""}`}
      />
    </Container>
  );
}